<template>
  <!-- 页面根容器：绑定当前主题类名，用于切换主题样式 -->
  <div class="order-list-page" :class="`theme-${currentTheme}`">
    <!-- 移动端顶部导航栏：仅在窗口宽度小于等于768px时显示 -->
    <div v-if="isMobile" class="mobile-top-header">
      <!-- 左侧：返回菜单和Logo -->
      <div class="mobile-header-left">
        <Icon type="ios-menu" class="menu-icon" @click="goBack" />
        <div class="logo-text">抖数君</div>
      </div>
      <!-- 右侧：主题切换器和用户信息 -->
      <div class="mobile-header-right">
        <Select v-model="currentTheme" @on-change="handleThemeChange" size="small" style="width: 100px;">
          <Option v-for="theme in themeOptions" :key="theme.name" :value="theme.name">
            {{ theme.label }}
          </Option>
        </Select>
        <div class="mobile-user-info" @click="toggleUserDropdown">
          <Avatar :src="require('@/assets/images/default-avatar.png')" size="small" />
          <span class="username">{{ username }}</span>
        </div>
      </div>
    </div>

    <!-- 页面标题：充值订单列表 -->
    <h2 class="page-title">充值订单列表</h2>

    <!-- 搜索框区域：用于按订单号搜索 -->
    <div class="search-bar">
      <!-- 订单号输入框：支持回车搜索和一键清空 -->
      <Input
          v-model="searchQuery"
          placeholder="请输入订单号搜索"
          :style="{ width: isMobile ? '100%' : '300px', marginRight: isMobile ? '0' : '10px' }"
          clearable
          @on-enter="fetchOrders"
      />
      <!-- 搜索按钮：点击触发搜索 -->
      <Button
          type="primary"
          :style="{ width: isMobile ? '100%' : 'auto' }"
          @click="fetchOrders"
          class="search-btn"
      >
        <Icon type="ios-search" class="btn-icon" />
        搜索
      </Button>
    </div>

    <!-- 订单表格区域：PC端显示表格，移动端显示卡片列表 -->
    <div class="table-container">
      <!-- 移动端卡片式订单列表 -->
      <div v-if="isMobile" class="mobile-order-list">
        <!-- 遍历当前页订单数据，生成卡片 -->
        <div
            v-for="order in dataItems"
            :key="order.id"
            class="mobile-order-card"
        >
          <!-- 卡片头部：套餐名称+支付状态 -->
          <div class="mobile-order-header">
            <span class="mobile-order-title">{{ order.package_name }}</span>
            <Tag v-if="order.status === 0" color="default">未支付</Tag>
            <Tag v-else-if="order.status === 1" color="success">已支付</Tag>
            <Tag v-else-if="order.status === 2" color="error">已取消</Tag>
          </div>
          <!-- 卡片主体：订单号、金额、创建时间 -->
          <div class="mobile-order-body">
            <p><strong>订单号：</strong>{{ order.out_trade_no }}</p>
            <p><strong>金额：</strong>￥{{ order.amount }}</p>
            <p><strong>时间：</strong>{{ order.created_at }}</p>
          </div>
          <!-- 卡片底部：操作按钮 -->
          <div class="mobile-order-footer">
            <Button
                v-if="order.status === 0"
                type="primary"
                size="small"
                @click="retryPay(order)"
                style="width: 100%;"
            >
              重新支付
            </Button>
          </div>
        </div>
      </div>
    
      <!-- PC端表格：使用 iView Table 组件 -->
      <Table
          v-else
          :columns="columns"
          :data="dataItems"
          border
          stripe
          class="order-table"
      >
        <!-- 自定义"支付状态"列：根据状态码显示不同颜色标签 -->
        <template #status="{ row }">
          <Tag v-if="row.status === 0" color="default">未支付</Tag>
          <Tag v-else-if="row.status === 1" color="success">已支付</Tag>
          <Tag v-else-if="row.status === 2" color="error">已取消</Tag>
        </template>
        <!-- 自定义"操作"列：未支付订单显示重新支付按钮 -->
        <template #action="{ row }">
          <Button
              v-if="row.status === 0"
              type="primary"
              size="small"
              @click="retryPay(row)"
              class="retry-btn"
          >
            重新支付
          </Button>
        </template>
      </Table>
    </div>

    <!-- 分页组件：上一页/下一页/页码信息 -->
    <div class="pagination">
      <!-- 上一页按钮：当前页为第一页时禁用 -->
      <button
          :disabled="currentPage === 1"
          class="page-btn"
          @click="prevPage"
      >
        上一页
      </button>

      <!-- 移动端分页信息：显示 当前页/总页数 和 总记录数 -->
      <div v-if="isMobile" class="page-info-mobile">
        <span>{{ currentPage }}/{{ totalPages }}</span>
        <span>共{{ total }}条</span>
      </div>

      <!-- PC端分页信息：显示第x页共y页 -->
      <span v-else class="page-info-text">第 {{ currentPage }} 页，共 {{ totalPages }} 页</span>

      <!-- 下一页按钮：当前页为最后一页时禁用 -->
      <button
          :disabled="currentPage === totalPages"
          class="page-btn"
          @click="nextPage"
      >
        下一页
      </button>

      <!-- PC端总记录数 -->
      <div v-if="!isMobile" class="page-info">
        共 {{ total }} 条记录
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/api/index'; // 引入API方法
import { getRechargeOrders } from '@/api/recharge'; // 引入API方法

export default {
  name: 'RechargeOrderList', // 组件名称
  data() {
    return {
      allDataItems: [], // 所有订单数据项
      currentPage: 1,  // 当前页码，默认为第一页
      pageSize: 5,     // 每页显示条数，默认4条
      total: 0,        // 数据总条数
      dataItems: [],   // 当前页显示的数据
      isMobile: false, // 是否为移动设备
      searchQuery: '', // 搜索框输入值
      username: localStorage.getItem('username') || '用户', // 用户名
      userDropdownVisible: false, // 用户下拉菜单显示状态
      currentTheme: localStorage.getItem('currentTheme') || 'starry', // 当前主题：默认与主页一致使用星空主题
      siteInfo: { site_name: '' }, // 站点信息（后台 API 返回，用于动态标题）
      themeOptions: [
        { name: 'default', label: '经典' },
        { name: 'starry', label: '星空' },
        { name: 'blue', label: '蓝色' },
        { name: 'purple', label: '紫色' },
        { name: 'green', label: '绿色' },
        { name: 'red', label: '红色' },
        { name: 'orange', label: '橙色' }
      ], // 主题选项
      columns: [       // 表格列配置
        {title: '订单号', key: 'out_trade_no', minWidth: this.isMobile ? 80 : 150}, // 订单号列
        {title: '套餐名称', key: 'package_name', align: 'center', minWidth: this.isMobile ? 80 : 120}, // 套餐名称列
        {title: '充值金额', key: 'amount', align: 'center', minWidth: this.isMobile ? 60 : 100}, // 充值金额列
        {
          title: '支付状态',
          slot: 'status', // 使用自定义插槽显示支付状态
          align: 'center',
          minWidth: this.isMobile ? 60 : 100,
          hidden: this.isMobile // 在移动端隐藏该列
        },
        {title: '创建时间', key: 'created_at', align: 'center', minWidth: this.isMobile ? 100 : 150}, // 创建时间列
        {
          title: '操作',
          slot: 'action', // 使用自定义插槽显示操作按钮
          align: 'center',
          minWidth: this.isMobile ? 80 : 120
        },
      ],
    };
  },
  computed: {
    // 计算总页数
    totalPages() {
      return Math.ceil(this.total / this.pageSize); // 总条数除以每页条数，向上取整
    },
  },
  created() {
    this.fetchOrders(); // 组件创建时获取订单数据
  },
  mounted() {
    this.checkLoginStatus();
    this.fetchSiteInfo(); // 获取站点信息并动态设置标题
    document.title = '充值列表'; // 初始标题，fetchSiteInfo 返回后用站点名称替换
    this.checkIsMobile(); // 检查是否为移动设备
    window.addEventListener('resize', this.handleResize); // 监听窗口大小变化
    // 应用保存的主题，默认与主页一致使用星空主题
    const savedTheme = localStorage.getItem('currentTheme') || 'starry';
    this.currentTheme = savedTheme;
    document.body.className = `theme-${savedTheme}`;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize); // 移除窗口大小变化监听，防止内存泄漏
  },
  methods: {
    /**
     * 获取站点信息（从后台 admin/getsite API）
     * 用后台配置的站点名称动态设置浏览器标题
     */
    async fetchSiteInfo() {
      try {
        const response = await apiClient.get('admin/getsite');
        if (response.data.code === 200) {
          this.siteInfo = response.data.data;
          document.title = (this.siteInfo.site_name || '抖数君') + '—充值列表';
        }
      } catch (error) {
        console.error('获取站点信息失败:', error);
      }
    },
    // 切换主题
    handleThemeChange(theme) {
      localStorage.setItem('currentTheme', theme);
      document.body.className = 'theme-' + theme;
    },
    // 切换用户下拉菜单
    toggleUserDropdown() {
      this.userDropdownVisible = !this.userDropdownVisible;
    },
    // 返回上一页
    goBack() {
      this.$router.back();
    },
    // 退出登录
    handleLogout() {
      localStorage.removeItem('userLoggedIn');
      localStorage.removeItem('username');
      localStorage.removeItem('userEmail');
      this.$router.push({ name: 'TiktokQuery' });
    },
    checkLoginStatus() {
      const isAuthenticated = localStorage.getItem('userLoggedIn') === 'true'; // 检查用户是否登录
      if (!isAuthenticated && this.$route.name !== 'TiktokQuery') {
        // 如果用户未登录且当前页面不是首页，则跳转到首页
        this.$router.push({name: 'TiktokQuery'});
      }
    },
    // 切换到上一页
    prevPage() {
      if (this.currentPage > 1) { // 如果当前页大于第一页
        this.currentPage--; // 当前页减1
        this.loadDataForPage(); // 加载对应页的数据
      }
    },

    // 切换到下一页
    nextPage() {
      if (this.currentPage < this.totalPages) { // 如果当前页小于总页数
        this.currentPage++; // 当前页加1
        this.loadDataForPage(); // 加载对应页的数据
      }
    },

    // 根据当前页码加载数据
    loadDataForPage() {
      const start = (this.currentPage - 1) * this.pageSize; // 计算起始索引
      const end = this.currentPage * this.pageSize; // 计算结束索引
      this.dataItems = this.allDataItems.slice(start, end); // 截取对应页的数据
    },

    // 检查是否为移动设备
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768; // 窗口宽度小于等于768时为移动设备
    },

    // 处理窗口大小变化（防抖）
    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer); // 清除之前的定时器
      this.resizeTimer = setTimeout(() => {
        this.checkIsMobile(); // 检查是否为移动设备
      }, 100); // 防抖延迟100ms
    },

    // 获取订单列表
    async fetchOrders() {
      try {
        const params = {
          user_email: localStorage.getItem('userEmail') || '', // 用户邮箱
          page: this.currentPage, // 当前页码
          pageSize: this.pageSize, // 每页条数
          out_trade_no: this.searchQuery || undefined, // 搜索关键词（可选）
        };
        const res = await getRechargeOrders(params); // 调用API获取订单数据
        if (res.data.code === 200) { // 判断响应状态码
          this.allDataItems = res.data.data.orders; // 更新所有订单数据
          this.total = res.data.data.total; // 更新总条数
          this.loadDataForPage(); // 根据当前页码加载数据
        } else {
          this.$Message.error(res.data.msg || '获取订单列表失败'); // 显示错误提示
        }
      } catch (error) {
        this.$Message.error('获取订单列表失败，请稍后重试'); // 捕获异常并显示错误提示
      }
    },

    // 重新支付
    retryPay(order) {
      this.$Modal.confirm({ // 显示确认对话框
        title: '确认重新支付',
        content: `您即将重新支付订单：${order.out_trade_no}，金额：￥${order.amount}`,
        onOk: async () => { // 用户确认后执行
          try {
            const response = await apiClient.post('/vip/create-order', { // 调用API创建VIP订单
              package_id: order.package_id, // 套餐ID
              type: 'alipay', // 默认支付宝支付
              user_email: localStorage.getItem('userEmail') || '', // 用户邮箱
              device_type: 'pc', // 设备类型
            });

            if (response.data.code === 1) { // 判断响应状态码
              window.open(response.data.data.pay_url, '_blank'); // 跳转到支付页面
            } else {
              this.$Message.error(response.data.msg || '订单创建失败'); // 显示错误提示
            }
          } catch (error) {
            this.$Message.error('重新支付失败，请稍后重试'); // 捕获异常并显示错误提示
          }
        },
      });
    },
  },
};
</script>
<style scoped>
/* ==================== 移动端顶部导航栏 ==================== */
.mobile-top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mobile-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-header-left .menu-icon {
  font-size: 24px;
  color: #fff;
  cursor: pointer;
}

.mobile-header-left .logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.mobile-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
}

.mobile-user-info .username {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}

/* ==================== 页面容器 ==================== */
.order-list-page {
  padding: 24px;
  min-height: 100vh;
  background-color: #f5f7fa;
  transition: background 0.3s ease;
}

/* ==================== 页面标题 ==================== */
.page-title {
  text-align: center;
  font-size: 26px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
  letter-spacing: 1px;
}

/* ==================== 搜索框区域 ==================== */
.search-bar {
  margin-bottom: 20px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

/* 搜索按钮图标 */
.btn-icon {
  margin-right: 4px;
}

/* ==================== 表格容器 ==================== */
.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* PC端表格 */
.order-table {
  width: 100%;
}

/* ==================== 分页组件 ==================== */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  gap: 12px;
  flex-wrap: wrap;
}

/* 分页按钮 */
.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  background: linear-gradient(135deg, #2d8cf0 0%, #5cadff 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.25);
}

.page-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(45, 140, 240, 0.35);
}

.page-btn:disabled {
  background: #c5c8ce;
  color: #fff;
  cursor: not-allowed;
  box-shadow: none;
}

.page-info-text,
.page-info {
  font-size: 14px;
  color: #606266;
}

/* ==================== 移动端卡片列表 ==================== */
.mobile-order-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
}

.mobile-order-card {
  background-color: #fff;
  border: 1px solid #e8eaec;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.mobile-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8eaec;
}

.mobile-order-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.mobile-order-body {
  margin-bottom: 12px;
}

.mobile-order-body p {
  margin: 8px 0;
  font-size: 14px;
  color: #515a6e;
  line-height: 1.5;
}

.mobile-order-body strong {
  color: #333;
  font-weight: 500;
}

.mobile-order-footer {
  padding-top: 12px;
  border-top: 1px solid #e8eaec;
}

/* ==================== 响应式适配 ==================== */
@media (max-width: 768px) {
  .order-list-page {
    padding: 12px;
  }

  .page-title {
    font-size: 22px;
    margin-bottom: 16px;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }

  .search-bar Input {
    width: 100% !important;
  }

  .search-bar Button {
    width: 100%;
  }

  .table-container {
    margin: 0 -12px;
    border-radius: 0;
  }

  .pagination {
    gap: 8px;
  }

  .page-btn {
    flex: 1;
    max-width: 100px;
  }

  .page-info {
    display: none;
  }
}

/* ==================== 经典主题 ==================== */
.theme-default .order-list-page {
  background-color: #f5f7fa !important;
}

.theme-default .page-title {
  color: #333 !important;
}

.theme-default .table-container {
  background: #ffffff !important;
}

.theme-default .page-info-text,
.theme-default .page-info {
  color: #606266 !important;
}

.theme-default .mobile-order-card {
  background: #ffffff !important;
  border-color: #e8eaec !important;
}

.theme-default .mobile-order-title {
  color: #333 !important;
}

.theme-default .mobile-order-body p {
  color: #515a6e !important;
}

.theme-default .mobile-order-body strong {
  color: #333 !important;
}

/* ==================== 星空主题（与主页一致）==================== */
.theme-starry .order-list-page {
  background: linear-gradient(135deg, #0c1445 0%, #1a237e 50%, #0d47a1 100%) !important;
}

.theme-starry .page-title {
  color: #e2e8f0 !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.theme-starry .table-container {
  background: rgba(22, 33, 62, 0.92) !important;
  border: 1px solid rgba(100, 255, 218, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35) !important;
  backdrop-filter: blur(10px);
}

/* 星空主题下表格文字颜色覆盖 */
.theme-starry .order-table >>> .ivu-table {
  color: #e2e8f0 !important;
  background: transparent !important;
}

.theme-starry .order-table >>> .ivu-table th,
.theme-starry .order-table >>> .ivu-table td {
  background: transparent !important;
  color: #e2e8f0 !important;
  border-color: rgba(100, 255, 218, 0.1) !important;
}

.theme-starry .order-table >>> .ivu-table-header th {
  background: rgba(15, 15, 26, 0.6) !important;
  color: #64ffda !important;
  font-weight: 600;
}

.theme-starry .order-table >>> .ivu-table-row:hover td {
  background: rgba(100, 255, 218, 0.08) !important;
}

.theme-starry .order-table >>> .ivu-table-stripe .ivu-table-row:nth-child(2n) td {
  background: rgba(255, 255, 255, 0.03) !important;
}

/* 星空主题搜索框 */
.theme-starry .search-bar >>> .ivu-input {
  background: #0f0f1a !important;
  color: #e2e8f0 !important;
  border-color: #2a2a3e !important;
}

.theme-starry .search-bar >>> .ivu-input::placeholder {
  color: #8892b0 !important;
}

.theme-starry .search-bar >>> .ivu-input:focus {
  border-color: #64ffda !important;
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15) !important;
}

/* 星空主题搜索按钮 */
.theme-starry .search-btn {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  border: none !important;
  color: #0a0e27 !important;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.3) !important;
}

.theme-starry .search-btn:hover {
  box-shadow: 0 6px 18px rgba(100, 255, 218, 0.4) !important;
  transform: translateY(-2px);
}

/* 星空主题重新支付按钮 */
.theme-starry .retry-btn {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  border: none !important;
  color: #0a0e27 !important;
}

/* 星空主题分页按钮 */
.theme-starry .page-btn {
  background: linear-gradient(135deg, #64ffda 0%, #00bcd4 100%) !important;
  color: #0a0e27 !important;
  box-shadow: 0 4px 12px rgba(100, 255, 218, 0.3) !important;
}

.theme-starry .page-btn:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(100, 255, 218, 0.4) !important;
}

.theme-starry .page-btn:disabled {
  background: #2a2a4e !important;
  color: #8892b0 !important;
}

.theme-starry .page-info-text,
.theme-starry .page-info {
  color: #e2e8f0 !important;
}

/* 星空主题移动端卡片 */
.theme-starry .mobile-order-card {
  background: rgba(22, 33, 62, 0.92) !important;
  border-color: rgba(100, 255, 218, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35) !important;
}

.theme-starry .mobile-order-title {
  color: #e2e8f0 !important;
}

.theme-starry .mobile-order-body p {
  color: #ccd6f6 !important;
}

.theme-starry .mobile-order-body strong {
  color: #64ffda !important;
}

/* ==================== 蓝色主题 ==================== */
.theme-blue .order-list-page {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
}

.theme-blue .page-title {
  color: #1565C0 !important;
}

.theme-blue .table-container {
  background: #ffffff !important;
}

.theme-blue .page-btn {
  background: linear-gradient(135deg, #1976D2 0%, #42A5F5 100%) !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

.theme-blue .page-info-text,
.theme-blue .page-info {
  color: #1565C0 !important;
}

/* ==================== 紫色主题 ==================== */
.theme-purple .order-list-page {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%) !important;
}

.theme-purple .page-title {
  color: #6A1B9A !important;
}

.theme-purple .table-container {
  background: #ffffff !important;
}

.theme-purple .page-btn {
  background: linear-gradient(135deg, #7B1FA2 0%, #AB47BC 100%) !important;
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.3) !important;
}

.theme-purple .page-info-text,
.theme-purple .page-info {
  color: #6A1B9A !important;
}

/* ==================== 绿色主题 ==================== */
.theme-green .order-list-page {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
}

.theme-green .page-title {
  color: #2E7D32 !important;
}

.theme-green .table-container {
  background: #ffffff !important;
}

.theme-green .page-btn {
  background: linear-gradient(135deg, #388E3C 0%, #66BB6A 100%) !important;
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.3) !important;
}

.theme-green .page-info-text,
.theme-green .page-info {
  color: #2E7D32 !important;
}

/* ==================== 红色主题 ==================== */
.theme-red .order-list-page {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%) !important;
}

.theme-red .page-title {
  color: #C62828 !important;
}

.theme-red .table-container {
  background: #ffffff !important;
}

.theme-red .page-btn {
  background: linear-gradient(135deg, #D32F2F 0%, #EF5350 100%) !important;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3) !important;
}

.theme-red .page-info-text,
.theme-red .page-info {
  color: #C62828 !important;
}

/* ==================== 橙色主题 ==================== */
.theme-orange .order-list-page {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%) !important;
}

.theme-orange .page-title {
  color: #E65100 !important;
}

.theme-orange .table-container {
  background: #ffffff !important;
}

.theme-orange .page-btn {
  background: linear-gradient(135deg, #F57C00 0%, #FFA726 100%) !important;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3) !important;
}

.theme-orange .page-info-text,
.theme-orange .page-info {
  color: #E65100 !important;
}
</style>
